import React, { useEffect, useMemo, useState } from 'react';
import axios from '../axios';
import { useToast } from '../components/ToastProvider';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Payout {
  id: string;
  amount: number;
  status: string;
  createdAt: string;
  processedAt?: string;
}

const PayoutManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();

  const fetchPayouts = async () => {
    const response = await axios.get('/api/admin/payouts');
    setPayouts(response.data || []);
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const updateStatus = async (id: string, status: string) => {
    try {
      setBusy(`${id}:${status}`);
      await axios.patch(`/api/admin/payouts/${id}/status`, { status });
      toast.show(t('payoutManagement.updated') as string, 'success');
      fetchPayouts();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('payoutManagement.updateError')), 'error');
    } finally { setBusy(null); }
  };

  const requested = useMemo(
    () => payouts.filter((p) => p.status === 'REQUESTED'),
    [payouts]
  );
  const approved = useMemo(
    () => payouts.filter((p) => p.status === 'APPROVED'),
    [payouts]
  );
  const done = useMemo(
    () => payouts.filter((p) => p.status === 'PROCESSED'),
    [payouts]
  );
  const rejected = useMemo(
    () => payouts.filter((p) => p.status === 'REJECTED'),
    [payouts]
  );

  const renderActions = (payout: Payout) => {
    if (payout.status === 'REQUESTED') {
      return (
        <>
          <button onClick={() => updateStatus(payout.id, 'APPROVED')} disabled={busy === `${payout.id}:APPROVED`} className="px-3 py-1 bg-[#47ABDB] disabled:opacity-60 text-white rounded text-sm">{t('payoutManagement.approve')}</button>
          <button onClick={() => updateStatus(payout.id, 'REJECTED')} disabled={busy === `${payout.id}:REJECTED`} className="px-3 py-1 bg-gray-200 disabled:opacity-60 text-gray-700 rounded text-sm">{t('payoutManagement.reject')}</button>
        </>
      );
    }
    if (payout.status === 'APPROVED') {
      return (
        <>
          <button onClick={() => updateStatus(payout.id, 'PROCESSED')} disabled={busy === `${payout.id}:PROCESSED`} className="px-3 py-1 bg-[#47ABDB] disabled:opacity-60 text-white rounded text-sm">{t('payoutManagement.markDone')}</button>
          <button onClick={() => updateStatus(payout.id, 'REJECTED')} disabled={busy === `${payout.id}:REJECTED`} className="px-3 py-1 bg-gray-200 disabled:opacity-60 text-gray-700 rounded text-sm">{t('payoutManagement.reject')}</button>
        </>
      );
    }
    return <span className="text-sm text-gray-500">{t('payoutManagement.noActions')}</span>;
  };

  const renderTable = (rows: Payout[], empty: string) => (
    <>
    <div className="space-y-3 p-4 md:hidden">
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-8 text-center text-base text-gray-500">{empty}</div>
      ) : (
        rows.map((payout) => (
          <MobileDataCard
            key={payout.id}
            title={t('payoutManagement.payoutLabel')}
            badge={<StatusBadge kind="payout" status={payout.status} />}
            subtitle={formatCurrency(payout.amount, i18n.resolvedLanguage)}
            rows={[
              { label: t('payoutManagement.requestedAt'), value: formatDate(payout.createdAt, i18n.resolvedLanguage) },
              { label: t('payoutManagement.processedAt'), value: payout.processedAt ? formatDate(payout.processedAt, i18n.resolvedLanguage) : '-' },
            ]}
            actions={renderActions(payout)}
          />
        ))
      )}
    </div>
    <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full text-left table-auto text-[15px] md:text-base">
        <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
          <tr>
            <th className="px-5 py-3 font-semibold">{t('payouts.amount')}</th>
            <th className="px-5 py-3 font-semibold">{t('payouts.status')}</th>
            <th className="px-5 py-3 font-semibold">{t('payoutManagement.requestedAt')}</th>
            <th className="px-5 py-3 font-semibold">{t('payoutManagement.processedAt')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.leads.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-5 py-8 text-center text-base text-gray-500">{empty}</td>
            </tr>
          ) : (
            rows.map((payout, idx) => (
              <tr key={payout.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                <td className="px-5 py-4">{formatCurrency(payout.amount, i18n.resolvedLanguage)}</td>
                <td className="px-5 py-4">
                  <StatusBadge kind="payout" status={payout.status} />
                </td>
                <td className="px-5 py-4">{formatDate(payout.createdAt, i18n.resolvedLanguage)}</td>
                <td className="px-5 py-4">{payout.processedAt ? formatDate(payout.processedAt, i18n.resolvedLanguage) : '-'}</td>
                <td className="px-5 py-4 space-x-2">{renderActions(payout)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    </>
  );

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('payoutManagement.title')}</h1>
      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('payoutManagement.requestedTitle')}</div>
            <div className="text-xs text-gray-600">{t('payoutManagement.requestedSubtitle')}</div>
          </div>
          <button onClick={fetchPayouts} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">{t('common.refresh')}</button>
        </div>
        {renderTable(requested, t('payoutManagement.requestedEmpty') as string)}
      </div>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('payoutManagement.approvedTitle')}</div>
          <div className="text-xs text-gray-600">{t('payoutManagement.approvedSubtitle')}</div>
        </div>
        {renderTable(approved, t('payoutManagement.approvedEmpty') as string)}
      </div>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('payoutManagement.completedTitle')}</div>
          <div className="text-xs text-gray-600">{t('payoutManagement.completedSubtitle')}</div>
        </div>
        {renderTable(done, t('payoutManagement.completedEmpty') as string)}
      </div>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('payoutManagement.rejectedTitle')}</div>
        </div>
        {renderTable(rejected, t('payoutManagement.rejectedEmpty') as string)}
      </div>
    </section>
  );
};

export default PayoutManagement;
